import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, Wallet, TrendingUp, TrendingDown, RefreshCw, Link } from 'lucide-react';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { useApp } from '../context/AppContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getTodayDate = () => new Date().toISOString().split('T')[0];

const formatNumber = (value) => {
  return new Intl.NumberFormat('ms-MY', {
    style: 'currency',
    currency: 'MYR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);
};

const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
};

const CashflowForm = ({ onSubmit, onClose, initialData }) => {
  const [formData, setFormData] = useState(initialData || {
    date: getTodayDate(),
    opening_balance: '',
    cash_in: '',
    cash_out: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      opening_balance: parseFloat(formData.opening_balance),
      cash_in: parseFloat(formData.cash_in),
      cash_out: parseFloat(formData.cash_out)
    });
  };

  const closingBalance = (parseFloat(formData.opening_balance) || 0) + (parseFloat(formData.cash_in) || 0) - (parseFloat(formData.cash_out) || 0);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Tarikh</label>
        <input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" required />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Opening Balance ($)</label>
        <input type="number" step="0.01" value={formData.opening_balance} onChange={(e) => setFormData({...formData, opening_balance: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" required placeholder="0.00" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Cash In ($)</label>
          <input type="number" step="0.01" value={formData.cash_in} onChange={(e) => setFormData({...formData, cash_in: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" required placeholder="0.00" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Cash Out ($)</label>
          <input type="number" step="0.01" value={formData.cash_out} onChange={(e) => setFormData({...formData, cash_out: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" required placeholder="0.00" />
        </div>
      </div>
      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-blue-700 text-sm">Closing Balance</p>
        <p className="text-xl font-bold text-blue-700">{formatNumber(closingBalance)}</p>
      </div>
      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Batal</button>
        <button type="submit" className="flex-1 px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 flex items-center justify-center gap-2">
          <Save className="w-4 h-4" /> Simpan
        </button>
      </div>
    </form>
  );
};

const CashflowPage = () => {
  const { cashflowList, addCashflow, updateCashflow, deleteCashflow, loading } = useApp();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewMode, setViewMode] = useState('auto'); // 'auto' or 'manual'
  const [period, setPeriod] = useState('monthly');
  const [autoSyncData, setAutoSyncData] = useState(null);
  const [autoSyncLoading, setAutoSyncLoading] = useState(false);

  // Fetch auto-sync data
  useEffect(() => {
    const fetchAutoSync = async () => {
      if (viewMode !== 'auto') return;
      
      setAutoSyncLoading(true);
      try {
        const response = await axios.get(`${API_URL}/api/cashflow/auto-sync?period=${period}`);
        setAutoSyncData(response.data);
      } catch (error) {
        console.error('Failed to fetch auto-sync data:', error);
      } finally {
        setAutoSyncLoading(false);
      }
    };
    fetchAutoSync();
  }, [viewMode, period]);

  // Ensure arrays
  const safeCashflowList = Array.isArray(cashflowList) ? cashflowList : [];
  
  // Choose data source based on view mode
  const chartData = viewMode === 'auto' 
    ? (autoSyncData?.data || [])
    : safeCashflowList.slice().sort((a, b) => new Date(a.date) - new Date(b.date));

  // Summary calculations
  const summary = viewMode === 'auto'
    ? autoSyncData?.summary || { total_cash_in: 0, total_cash_out: 0, net_cashflow: 0, current_balance: 0 }
    : {
        total_cash_in: safeCashflowList.reduce((sum, item) => sum + (item.cash_in || 0), 0),
        total_cash_out: safeCashflowList.reduce((sum, item) => sum + (item.cash_out || 0), 0),
        net_cashflow: safeCashflowList.reduce((sum, item) => sum + (item.cash_in || 0) - (item.cash_out || 0), 0),
        current_balance: safeCashflowList.length > 0 ? safeCashflowList[0].closing_balance || 0 : 0
      };

  const handleAdd = async (data) => {
    await addCashflow(data);
    setModalOpen(false);
  };

  const handleEdit = async (data) => {
    await updateCashflow(editingItem.id, data);
    setEditingItem(null);
    setModalOpen(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Padam entry cashflow ini?')) {
      await deleteCashflow(id);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Cashflow</h1>
          <p className="text-gray-500 text-sm">Jejak aliran tunai syarikat</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          {/* View Mode Toggle */}
          <div className="flex items-center gap-1 sm:gap-2 bg-white border border-gray-200 rounded-lg p-1">
            <button
              onClick={() => setViewMode('auto')}
              className={`px-2.5 sm:px-3 py-2 text-xs sm:text-sm rounded-md transition-colors flex items-center gap-1 ${
                viewMode === 'auto' ? 'bg-green-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Link className="w-4 h-4" /> Auto-Sync
            </button>
            <button
              onClick={() => setViewMode('manual')}
              className={`px-2.5 sm:px-3 py-2 text-xs sm:text-sm rounded-md transition-colors ${
                viewMode === 'manual' ? 'bg-red-700 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Manual
            </button>
          </div>

          {/* Period Selector (only for auto mode) */}
          {viewMode === 'auto' && (
            <div className="flex items-center gap-1 sm:gap-2 bg-white border border-gray-200 rounded-lg p-1">
              {['monthly', 'quarterly', 'yearly'].map(p => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  className={`px-2.5 sm:px-3 py-2 text-xs sm:text-sm rounded-md transition-colors whitespace-nowrap ${
                    period === p ? 'bg-red-700 text-white' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {p === 'monthly' ? 'Bulanan' : p === 'quarterly' ? 'Suku Tahun' : 'Tahunan'}
                </button>
              ))}
            </div>
          )}

          {viewMode === 'manual' && (
            <button onClick={() => { setEditingItem(null); setModalOpen(true); }}
              className="px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 flex items-center gap-2">
              <Plus className="w-4 h-4" /> Tambah Entry
            </button>
          )}
        </div>
      </div>

      {/* Auto-Sync Info Banner */}
      {viewMode === 'auto' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <RefreshCw className="w-5 h-5 text-green-600" />
          <div>
            <p className="text-green-800 font-medium">Auto-Sync Mode</p>
            <p className="text-green-600 text-sm">Data cashflow dijana automatik dari Revenue & Expenses. Tiada manual entry diperlukan.</p>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Wallet className="w-5 h-5 text-red-700" />
            <span className="text-gray-500 text-sm">Baki Semasa</span>
          </div>
          <p className="text-xl font-bold text-gray-800">{formatNumber(summary.current_balance)}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            <span className="text-gray-500 text-sm">Jumlah Cash In</span>
          </div>
          <p className="text-xl font-bold text-emerald-600">{formatNumber(summary.total_cash_in)}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <TrendingDown className="w-5 h-5 text-red-600" />
            <span className="text-gray-500 text-sm">Jumlah Cash Out</span>
          </div>
          <p className="text-xl font-bold text-red-600">{formatNumber(summary.total_cash_out)}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Wallet className="w-5 h-5 text-blue-600" />
            <span className="text-gray-500 text-sm">Net Cashflow</span>
          </div>
          <p className={`text-xl font-bold ${summary.net_cashflow >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {formatNumber(summary.net_cashflow)}
          </p>
        </div>
      </div>

      {/* Cashflow Chart */}
      <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-100 shadow-sm">
        <h3 className="text-gray-800 font-medium mb-4">
          Trend Cashflow {viewMode === 'auto' ? `(${period === 'monthly' ? 'Bulanan' : period === 'quarterly' ? 'Suku Tahunan' : 'Tahunan'})` : ''}
        </h3>
        {autoSyncLoading ? (
          <div className="h-[300px] flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-700"></div>
          </div>
        ) : chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey={viewMode === 'auto' ? 'period' : 'date'} tick={{ fill: '#6b7280', fontSize: 10 }} />
              <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} tickFormatter={(v) => `RM${(v/1000).toFixed(0)}k`} />
              <Tooltip 
                formatter={(value, name) => [formatNumber(value), name === 'cash_in' ? 'Cash In' : name === 'cash_out' ? 'Cash Out' : 'Balance']}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }} 
              />
              <Bar dataKey="cash_in" fill="#10B981" name="Cash In" />
              <Bar dataKey="cash_out" fill="#EF4444" name="Cash Out" />
              <Line type="monotone" dataKey="closing_balance" stroke="#C41E3A" strokeWidth={2} name="Balance" dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[300px] flex items-center justify-center text-gray-400">
            Tiada data cashflow
          </div>
        )}
      </div>

      {/* Net Flow Chart */}
      {viewMode === 'auto' && chartData.length > 0 && (
        <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-100 shadow-sm">
          <h3 className="text-gray-800 font-medium mb-4">Net Cashflow per {period === 'monthly' ? 'Bulan' : period === 'quarterly' ? 'Suku' : 'Tahun'}</h3>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="period" tick={{ fill: '#6b7280', fontSize: 10 }} />
              <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} tickFormatter={(v) => `RM${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(value) => [formatNumber(value), 'Net']} />
              <Area 
                type="monotone" 
                dataKey="net" 
                stroke="#3B82F6" 
                fill="#3B82F6" 
                fillOpacity={0.1} 
                strokeWidth={2} 
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Cashflow Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">
            {viewMode === 'auto' ? 'Ringkasan Cashflow (Auto-Generated)' : 'Senarai Entry Manual'}
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-3 text-gray-600 font-medium">{viewMode === 'auto' ? 'Tempoh' : 'Tarikh'}</th>
                <th className="text-right p-3 text-gray-600 font-medium">Opening</th>
                <th className="text-right p-3 text-gray-600 font-medium">Cash In</th>
                <th className="text-right p-3 text-gray-600 font-medium">Cash Out</th>
                <th className="text-right p-3 text-gray-600 font-medium">Net</th>
                <th className="text-right p-3 text-gray-600 font-medium">Closing</th>
                {viewMode === 'manual' && <th className="text-center p-3 text-gray-600 font-medium">Tindakan</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {chartData.map((item, idx) => (
                <tr key={item.id || idx} className="hover:bg-gray-50">
                  <td className="p-3 text-gray-700">{item.period || item.date}</td>
                  <td className="p-3 text-right text-gray-600">{formatNumber(item.opening_balance)}</td>
                  <td className="p-3 text-right text-emerald-600 font-medium">+{formatNumber(item.cash_in)}</td>
                  <td className="p-3 text-right text-red-600 font-medium">-{formatNumber(item.cash_out)}</td>
                  <td className={`p-3 text-right font-medium ${(item.net || (item.cash_in - item.cash_out)) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatNumber(item.net || (item.cash_in - item.cash_out))}
                  </td>
                  <td className="p-3 text-right text-gray-800 font-bold">{formatNumber(item.closing_balance)}</td>
                  {viewMode === 'manual' && (
                    <td className="p-3">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => { setEditingItem(item); setModalOpen(true); }}
                          className="p-1.5 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-700">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(item.id)}
                          className="p-1.5 hover:bg-red-50 rounded text-gray-400 hover:text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {chartData.length === 0 && (
                <tr><td colSpan={viewMode === 'manual' ? 7 : 6} className="p-8 text-center text-gray-500">Tiada data cashflow</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={modalOpen} onClose={() => { setModalOpen(false); setEditingItem(null); }} title={editingItem ? 'Edit Cashflow' : 'Tambah Cashflow Entry'}>
        <CashflowForm onSubmit={editingItem ? handleEdit : handleAdd} onClose={() => { setModalOpen(false); setEditingItem(null); }} initialData={editingItem} />
      </Modal>
    </div>
  );
};

export default CashflowPage;
